import React, { useCallback } from "react";
import { Dropdown, Spinner } from "react-bootstrap";

import { Icon } from "@/components/Icon";
import { ReportProgressCallback } from "@/lib/gas-api";
import { SyncTime } from "./SyncTime";

export interface ManualSyncButtonProps {
  label: string;
  lastSynced: Date | null | undefined;
  runSync: (reportProgress: ReportProgressCallback) => Promise<void>;
  syncRunning: boolean;
  setSyncRunning: (value: boolean) => void;
}

const successStatus = "Sync Complete - Refresh page to see changes";
const errorStatusPrefix = "Error: ";

export const ManualSyncButton: React.FC<ManualSyncButtonProps> = ({
  label,
  lastSynced,
  runSync,
  syncRunning,
  setSyncRunning,
}) => {
  const [syncStatus, setSyncStatusInternal] = React.useState("");

  const setSyncStatus = useCallback(
    (status: string) => {
      setSyncStatusInternal(status);
      console.info(`Status update for ${label} sync: ${status}`);
    },
    [label],
  );

  const syncSuccess = syncStatus === successStatus;
  const syncError = syncStatus.startsWith(errorStatusPrefix);

  const onClick = useCallback(() => {
    if (syncRunning) return;
    setSyncRunning(true);
    setSyncStatus("Starting Sync...");
    void runSync(setSyncStatus)
      .then(() => {
        setSyncStatus(successStatus);
        setTimeout(() => {
          if (syncStatus === successStatus) setSyncStatus("");
        }, 5000);
      })
      .catch((err: unknown) => {
        console.error(err);
        setSyncStatus(errorStatusPrefix + (err as Error).message);
      })
      .finally(() => {
        setSyncRunning(false);
      });
  }, [syncRunning, setSyncRunning, runSync, syncStatus, setSyncStatus]);

  const icon =
    syncRunning ? <Spinner className="me-2" size="sm" />
    : syncError ? <Icon icon="exclamation-circle-fill" className="col-auto mt-1 me-2 text-danger" />
    : <Icon icon="arrow-repeat" className="col-auto mt-1 me-2" />;

  const syncStatusClass =
    syncError ? "text-wrap text-danger-emphasis"
    : syncSuccess ? "text-success-emphasis"
    : "text-secondary";

  return (
    <Dropdown.Item onClick={onClick} className="text-wrap text-md-nowrap" disabled={syncRunning}>
      <div className="d-flex align-items-center">
        <div>{icon}</div>
        <span>
          {label}
          {!syncStatus && (
            <span className="text-secondary">
              <br />
              Last Synced: <SyncTime lastUpdated={lastSynced} />
            </span>
          )}
          {syncStatus && (
            <span className={syncStatusClass}>
              <br />
              {syncStatus}
            </span>
          )}
        </span>
      </div>
    </Dropdown.Item>
  );
};
